import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

export interface ComposeMoreMenuItem {
	id: string;
	label: string;
	icon: IconDefinition;
	disabled?: boolean;
	onSelect: () => void;
}

interface ComposeMoreMenuProps {
	items: ComposeMoreMenuItem[];
	/**
	 * Portal target for the menu. Defaults to `document.body` (web app). The Chrome
	 * extension passes its shadow-root container so the menu renders inside the
	 * isolated style scope instead of the (unstyled) host page.
	 */
	portalContainer?: HTMLElement | null;
}

const MENU_WIDTH = 220;
const ESTIMATED_MENU_HEIGHT = 120;

/**
 * Overflow ("3-dot") menu for the inbox chat composer toolbar. Collects the
 * secondary compose actions (e.g. "Tell AI what to write", "Send test") behind
 * a single icon trigger. Mirrors the portal pattern of `VariablePicker`
 * (rendered into `document.body` so it is not clipped by the chat's scroll
 * containers — see CLAUDE.md rule 11).
 */
export const ComposeMoreMenu: FunctionComponent<ComposeMoreMenuProps> = ({
	items,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const openUpward = spaceBelow < ESTIMATED_MENU_HEIGHT && rect.top > spaceBelow;
		// Right-align the menu to the trigger, flipping to left-aligned when it
		// would overflow the left viewport edge.
		const left = Math.max(8, rect.right - MENU_WIDTH);

		if (openUpward) {
			setMenuStyle({
				position: "fixed",
				bottom: window.innerHeight - rect.top + 4,
				left,
			});
		} else {
			setMenuStyle({
				position: "fixed",
				top: rect.bottom + 4,
				left,
			});
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
				return;
			}

			setIsOpen(false);
		};

		if (isOpen) {
			updatePosition();
			document.addEventListener("mousedown", handleClickOutside);
			window.addEventListener("resize", updatePosition);
			window.addEventListener("scroll", updatePosition, true);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen, updatePosition]);

	const handleSelect = useCallback((item: ComposeMoreMenuItem) => {
		if (item.disabled) {
			return;
		}

		item.onSelect();
		setIsOpen(false);
	}, []);

	const menu = useMemo(() => {
		if (!isOpen) {
			return null;
		}

		return ReactDOM.createPortal(
			<div
				ref={menuRef}
				className="bg-white border border-border-light rounded-lg shadow-lg py-1 flex flex-col z-[9999]"
				data-testid="compose-more-menu"
				style={{ ...menuStyle, width: MENU_WIDTH }}
			>
				{items.map((item) => (
					<button
						key={item.id}
						className={classNames(
							"w-full px-3 py-2 text-left text-xs flex items-center gap-2",
							item.disabled
								? "text-text-meta cursor-not-allowed opacity-60"
								: "text-text hover:bg-brand-white"
						)}
						disabled={item.disabled}
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleSelect(item);
						}}
					>
						<Icon className="w-3.5 h-3.5 shrink-0" icon={item.icon} />
						<span className="truncate">{item.label}</span>
					</button>
				))}
			</div>,
			portalContainer ?? document.body
		);
	}, [isOpen, menuStyle, items, handleSelect, portalContainer]);

	if (items.length === 0) {
		return null;
	}

	return (
		<div ref={triggerRef} className="w-fit">
			<button
				aria-label={t("inbox.composeMenu.more")}
				className="h-[28px] px-2 flex items-center justify-center bg-white hover:shadow-md border border-gray-200 text-text rounded-[10px] duration-200"
				title={t("inbox.composeMenu.more")}
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<Icon className="w-3.5 h-3.5" icon={faEllipsis} />
			</button>
			{menu}
		</div>
	);
};
